import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Play, Save, Send, RefreshCw, Lock, ExternalLink, List, LayoutGrid, XCircle, FlaskConical } from 'lucide-react';
import useBulkSelect, { bulkDelete } from '@/hooks/useBulkSelect';
import BulkDeleteBar from '@/components/BulkDeleteBar';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { isRecent } from '@/lib/uxHelpers';
import { useStats } from '@/contexts/StatsContext';
import { markListSeen, ENTITY_TYPES } from '@/lib/readState';
import { toastApiError } from '@/lib/errors';
import RefLink from '@/components/RefLink';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import ChipFilter from '@/components/ChipFilter';
import useLaboratories from '@/hooks/useLaboratories';

// Kanban sloupce dle status enum. Bez drag (status mění action
// buttony — metrologická disciplína, ne drag-freestyle).
const KANBAN_STATUSES = [
  { status: 'pending_intake', tone: 'info' },
  { status: 'in_progress', tone: 'warning' },
  { status: 'completed', tone: 'info' },
  { status: 'pending_approval', tone: 'warning' },
  { status: 'approved', tone: 'success' },
  { status: 'rejected', tone: 'danger' },
];

// Klíče pro kanban statusy v i18n
const KANBAN_STATUS_LABEL_KEYS = {
  pending_intake: 'calibrations.kanban-status-pending-intake',
  in_progress: 'calibrations.kanban-status-in-progress',
  completed: 'calibrations.kanban-status-completed',
  pending_approval: 'calibrations.kanban-status-pending-approval',
  approved: 'calibrations.kanban-status-approved',
  rejected: 'calibrations.kanban-status-rejected',
};

const TONE_BORDER = {
  info: 'var(--ct-info)',
  warning: 'var(--ct-warning)',
  success: 'var(--ct-success)',
  danger: 'var(--ct-error)',
};

const TONE_SOFT = {
  info: 'var(--ct-info-soft)',
  warning: 'var(--ct-warning-soft)',
  success: 'var(--ct-success-soft)',
  danger: 'var(--ct-error-soft)',
};

// Priority a assigned-to badges
const PRIORITY_TONES = {
  high: { bg: 'var(--ct-error-soft)', fg: 'var(--ct-error)' },
  normal: { bg: 'var(--ct-bg-secondary)', fg: 'var(--ct-text-secondary)' },
  low: { bg: 'var(--ct-info-soft)', fg: 'var(--ct-info)' },
};

function KanbanCard({ cal, tone, onClick }) {
  const { t } = useTranslation();
  const priority = cal.priority || 'normal';
  const priorityTone = PRIORITY_TONES[priority] || PRIORITY_TONES.normal;
  const priorityLabel = t(`calibrations.priority-${priority}`);
  const accent = TONE_BORDER[tone] || 'var(--ct-accent)';
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left ct-panel p-3 mb-2 transition-all duration-200"
      style={{ boxShadow: 'var(--ct-shadow-sm)', borderLeftWidth: '3px', borderLeftColor: accent }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--ct-shadow-md)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--ct-shadow-sm)'; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Priority + assignee top row */}
      {(priority !== 'normal' || cal.assignedToName) && (
        <div className="flex items-center justify-between mb-1 gap-1">
          {priority !== 'normal' ? (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase"
              style={{ background: priorityTone.bg, color: priorityTone.fg }}>
              ↑ {priorityLabel}
            </span>
          ) : <span />}
          {cal.assignedToName && (
            <span className="text-[10px] px-1.5 py-0.5 rounded"
              style={{ background: 'var(--ct-bg-secondary)', color: 'var(--ct-text-secondary)' }}
              title={t('calibrations.card-assignee-tooltip', { name: cal.assignedToName })}>
              👤 {cal.assignedToName.split(' ')[0]}
            </span>
          )}
        </div>
      )}
      <div className="font-medium text-sm truncate" style={{ color: 'var(--ct-text)' }}>
        {cal.instrumentName || t('calibrations.card-instrument-fallback')}
      </div>
      <div className="text-xs truncate mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>
        {cal.instrumentType || ''}
      </div>
      {/* Sonda jako sekundární řádek (kód + rozsah). */}
      {cal.probeCode && (
        <div className="text-[11px] truncate mt-0.5 font-mono" style={{ color: 'var(--ct-accent)' }}>
          {cal.probeCode}
          {(cal.probeRangeMin !== undefined && cal.probeRangeMax !== undefined) && (
            <span style={{ color: 'var(--ct-text-disabled)' }}>
              {` ${cal.probeRangeMin}–${cal.probeRangeMax} ${cal.probeUnitSymbol || ''}`}
            </span>
          )}
        </div>
      )}
      {cal.customerName && (
        <div className="text-xs truncate mt-1" style={{ color: 'var(--ct-text-disabled)' }}>
          {cal.customerName}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {cal.orderNumber && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'var(--ct-accent-soft)', color: 'var(--ct-accent)' }}>
            {cal.orderNumber}
          </span>
        )}
        {cal.certificateNumber && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'var(--ct-success-soft)', color: 'var(--ct-success)' }}>
            {cal.certificateNumber}
          </span>
        )}
        {cal.result && <StatusBadge status={cal.result} />}
      </div>
    </button>
  );
}

function KanbanView({ calibrations, onCardClick }) {
  const { t } = useTranslation();
  return (
    <div className="flex-1 overflow-x-auto overflow-y-hidden">
      <div className="flex gap-3 h-full min-w-max pb-2">
        {KANBAN_STATUSES.map((col) => {
          const items = calibrations.filter(c => c.status === col.status);
          return (
            <div
              key={col.status}
              className="flex flex-col w-[260px] flex-shrink-0 rounded-lg overflow-hidden"
              style={{ background: 'var(--ct-bg-secondary)', border: '1px solid var(--ct-border)' }}
            >
              <div
                className="px-3 py-2.5 flex items-center justify-between sticky top-0 z-10"
                style={{ borderTop: `3px solid ${TONE_BORDER[col.tone]}`, background: TONE_SOFT[col.tone] }}
              >
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: TONE_BORDER[col.tone] }}>
                  {t(KANBAN_STATUS_LABEL_KEYS[col.status])}
                </span>
                <span
                  className="text-[11px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center"
                  style={{ background: TONE_BORDER[col.tone], color: '#fff' }}
                >
                  {items.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {items.length === 0 ? (
                  <div className="text-xs text-center py-6" style={{ color: 'var(--ct-text-disabled)' }}>
                    {t('calibrations.kanban-column-empty')}
                  </div>
                ) : (
                  items.map(c => <KanbanCard key={c.id} cal={c} tone={col.tone} onClick={() => onCardClick(c)} />)
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CalibrationPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Single source of truth — seznam laboratoří z hooku (GET /api/laboratories).
  // Prázdné pole při prvním renderu, doplní se po načtení (modul-cache).
  const labs = useLaboratories();
  const LAB_IDS = labs.map((l) => l.code);
  // code → name mapa pro jednotné labely napříč stránkami (např. "Srážky").
  const LAB_LABEL = Object.fromEntries(labs.map((l) => [l.code, l.name]));
  const LABS = LAB_IDS.map(id => ({ id, label: LAB_LABEL[id] || id }));
  const [searchParams, setSearchParams] = useSearchParams();
  const hasPermission = (action, lab) => {
    if (!user) return false;
    if (user.isAdmin) return true;
    const perm = (user.permissions || []).find(p => p.module === lab);
    return perm?.actions?.includes(action) || false;
  };
  const visibleLabs = LABS.filter(l => hasPermission('view', l.id));
  const [selectedLab, setSelectedLab] = useState(visibleLabs[0]?.id || 'teplota');
  const canCalibrate = hasPermission('calibrate', selectedLab);
  const [calibrations, setCals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  // UX vrstva C: filtry napříč seznamem kalibrací.
  const [filterText, setFilterText] = useState('');
  // Status teď multi-select (array) místo single dropdown.
  const [filterStatuses, setFilterStatuses] = useState([]);
  // View toggle mezi list (split-view) a kanban, persist do localStorage.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('ct-cal-view') || 'list'; } catch { return 'list'; }
  });
  const setView = (v) => {
    setViewMode(v);
    try { localStorage.setItem('ct-cal-view', v); } catch {}
  };
  const [etalons, setEtalons] = useState([]);
  const [methodologies, setMethodologies] = useState([]);
  const [intakes, setIntakes] = useState([]);
  // Počet 'pending_intake' kalibrací per laboratoř (badge na tabech).
  const [pendingByLab, setPendingByLab] = useState({});
  // Sondy patřící přístroji vybrané kalibrace.
  const [probesForInstrument, setProbesForInstrument] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [dialogIntakeId, setDialogIntakeId] = useState('');
  const [dialogInstrumentIdx, setDialogInstrumentIdx] = useState('');
  // `measurements` zůstává — editor tabulka + onMethodologyChange + saveCal PUT payload.
  // Měření samotné (HW provider, simulace) je v dedikované routě /calibrations/:id/run.
  const [measurements, setMeasurements] = useState({ points: [] });

  const { refetch: refetchStats } = useStats();

  const load = useCallback(() => {
    api.get(`/calibrations?laboratory=${selectedLab}`).then(r => setCals(r.data)).catch(() => {});
    api.get('/etalons').then(r => setEtalons(r.data)).catch(() => {});
    api.get(`/methodologies?laboratory=${selectedLab}`).then(r => setMethodologies(r.data)).catch(() => {});
    api.get('/intakes').then(r => setIntakes(r.data)).catch(() => {});
    // Napříč laboratořemi: kolik kalibrací čeká na start (pending_intake) — pro badge na tabech.
    // (Stránka jinak zobrazuje jen vybranou laboratoř; badge řeší nesoulad s dashboardem.)
    api.get('/calibrations').then(r => {
      const m = {};
      (r.data || []).forEach(c => {
        if (c.status === 'pending_intake') m[c.laboratory] = (m[c.laboratory] || 0) + 1;
      });
      setPendingByLab(m);
    }).catch(() => {});
  }, [selectedLab]);

  useEffect(() => { load(); }, [load]);

  // Badge = calibrace ve stavu pending_intake. Po načtení listu mark-seen.
  useEffect(() => {
    if (calibrations.length === 0) return;
    const pendingIntakeIds = calibrations.filter(c => c.status === 'pending_intake').map(c => c.id).filter(Boolean);
    if (pendingIntakeIds.length === 0) return;
    markListSeen(ENTITY_TYPES.CALIBRATION, pendingIntakeIds).then(() => refetchStats());
  }, [calibrations, refetchStats]);

  // `?edit=<id>` (z CalibrationDetailPage „Upravit", kanban, search).
  // Pozn.: dedikovaná routa `/calibrations/:id/run` nahradila starý `&start=1` flow.
  const editParam = searchParams.get('edit');
  useEffect(() => {
    if (!editParam) return;
    const existing = calibrations.find(c => c.id === editParam);
    if (existing) {
      selectCal(existing);
      setView('list');
      setSearchParams({});
      return;
    }
    // Kalibrace není v aktuálním lab seznamu — dohledat a přepnout lab.
    api.get(`/calibrations/${editParam}`)
      .then(r => {
        if (r.data?.laboratory && r.data.laboratory !== selectedLab) {
          setSelectedLab(r.data.laboratory);
        } else {
          // Stejná laborka, ale v listu není (ještě se nenačetl nebo permission)
          setSearchParams({});
        }
      })
      .catch(() => setSearchParams({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam, calibrations]);

  const selectCal = async (c) => {
    setSelected(c.id);
    setForm({ ...c });
    // Načti sondy patřící přístroji, ať je dropdown připraven.
    if (c.instrumentId) {
      api.get(`/probes?instrumentId=${c.instrumentId}`)
        .then(r => setProbesForInstrument(r.data))
        .catch(() => setProbesForInstrument([]));
    } else {
      setProbesForInstrument([]);
    }
    // ADR-007: pokud měření existují, zobraz je. Jinak dopočítej pevné body
    // z metodiky — jinak by uložení metadat (např. etalonu) poslalo prázdná
    // měření a backend by je shodil (0 ≠ N). Týká se kalibrací s už přiřazenou
    // sondou nebo otevřených po reloadu (onProbeChange se v takovém případě
    // nespustí). Zrcadlí logiku onProbeChange a CalibrationRunScreen.
    if (c.measurements?.points?.length) {
      setMeasurements(c.measurements);
      return;
    }
    setMeasurements({ points: [] });
    if (!c.methodology) return;
    try {
      const mr = await api.get(`/methodologies/${c.methodology}`);
      const calPoints = mr.data?.extractedData?.calibrationPoints?.points || [];
      if (!calPoints.length) return;
      const seriesCount = mr.data?.extractedData?.series?.count || 3;
      setMeasurements({
        points: calPoints.map(nominal => ({
          nominal,
          series: Array.from({ length: seriesCount }, () => ({ ascending: 0, descending: 0 })),
          average: 0,
          error: 0,
          hysteresis: 0,
        })),
      });
    } catch {
      // Ponech prázdné — fix C na backendu prázdná měření při uložení pustí.
    }
  };

  // ADR-006: nová kalibrace musí vyjít z příjmu odeslaného ke kalibraci.
  const openCreateDialog = () => {
    setDialogIntakeId('');
    setDialogInstrumentIdx('');
    setCreateDialogOpen(true);
  };

  const availableIntakes = intakes.filter(i => i.sentToCalibration);
  const selectedDialogIntake = availableIntakes.find(i => i.id === dialogIntakeId);
  const dialogInstruments = selectedDialogIntake?.instruments || [];

  const confirmCreate = async () => {
    if (!dialogIntakeId || dialogInstrumentIdx === '') return;
    const inst = dialogInstruments[parseInt(dialogInstrumentIdx, 10)];
    if (!inst) return;
    try {
      const res = await api.post('/calibrations', {
        laboratory: selectedLab,
        intakeId: dialogIntakeId,
        instrumentName: inst.name || '',
        instrumentType: inst.type || '',
        instrumentManufacturer: inst.manufacturer || '',
        instrumentMarking1: inst.marking1 || '',
        instrumentMarking2: inst.marking2 || '',
        notes: '',
      });
      setCreateDialogOpen(false);
      load();
      selectCal(res.data);
      toast.success(t('calibrations.toast-created'));
    } catch (err) {
      toastApiError(err);
    }
  };

  const saveCal = async () => {
    if (!selected) return;
    try {
      await api.put(`/calibrations/${selected}`, { ...form, measurements });
      load();
      // UX vrstva E: pokud je kalibrace ve stavu, kdy lze odeslat ke schválení, nabídni další krok.
      const canSubmit = ['in_progress', 'completed'].includes(form.status);
      if (canSubmit) {
        toast.success(t('calibrations.toast-saved'), {
          action: {
            label: t('calibrations.toast-saved-action-submit'),
            onClick: () => submitForApproval(),
          },
        });
      } else {
        toast.success(t('calibrations.toast-saved'));
      }
    } catch (err) { toastApiError(err); }
  };

  const submitForApproval = async () => {
    if (!selected) return;
    try {
      await api.put(`/calibrations/${selected}/submit-approval`);
      load();
      refetchStats();
      // UX vrstva E: po odeslání nabídnout otevření schvalovacího procesu.
      toast.success(t('calibrations.toast-submitted'), {
        action: {
          label: t('calibrations.toast-submitted-action-open'),
          onClick: () => navigate('/approvals'),
        },
      });
    } catch (err) { toastApiError(err); }
  };

  const startRework = async () => {
    if (!selected) return;
    try {
      // Přepnutí status z rejected na in_progress + vyčištění rejectionReason.
      // Backend update_calibration přijímá partial set — pošleme minimální payload.
      await api.put(`/calibrations/${selected}`, {
        status: 'in_progress',
        rejectionReason: '',
      });
      load();
      toast.success(t('calibrations.toast-rework-started'));
      setForm(f => ({ ...f, status: 'in_progress', rejectionReason: '' }));
    } catch {
      toast.error(t('calibrations.toast-error-default'));
    }
  };

  // ADR-007: změna sondy = změna metodiky → reset měření.
  // Sonda určuje metodiku. Backend re-denormalizuje vše
  // při PUT /calibrations/{id} {probeId} a vrátí kompletní kalibraci.
  const onProbeChange = async (probeId) => {
    if (probeId === (form.probeId || '')) return;
    const hasMeasurements = (measurements.points || []).some(pt =>
      (pt.series || []).some(s => (s.ascending && s.ascending !== 0) || (s.descending && s.descending !== 0))
    );
    if (hasMeasurements) {
      const ok = window.confirm(t('calibrations.confirm-probe-change'));
      if (!ok) return;
    }
    try {
      const res = await api.put(`/calibrations/${selected}`, { probeId });
      // Re-populate form + measurements z čerstvé kalibrace.
      setForm({ ...res.data });
      setMeasurements({ points: [] });
      // Auto-load body z nové metodiky.
      const meth = methodologies.find(m => m.id === res.data.methodology);
      if (meth?.extractedData?.calibrationPoints?.points) {
        const calPoints = meth.extractedData.calibrationPoints.points;
        const seriesCount = meth.extractedData?.series?.count || 3;
        const pts = calPoints.map(nominal => ({
          nominal,
          series: Array.from({ length: seriesCount }, () => ({ ascending: 0, descending: 0 })),
          average: 0,
          error: 0,
          hysteresis: 0,
        }));
        setMeasurements({ points: pts });
      }
      load();
      toast.success(t('calibrations.toast-probe-changed'));
    } catch (err) {
      toastApiError(err, t('calibrations.toast-error-probe'));
    }
  };

  // ADR-007: po startu kalibrace je sonda (a tím metodika) locked.
  const probeLocked = !!form.status && form.status !== 'pending_intake';

  // UX vrstva C: filter calibrací — text napříč fields + status (multi-select).
  const filteredCalibrations = calibrations.filter(c => {
    if (filterStatuses.length > 0 && !filterStatuses.includes(c.status)) return false;
    if (filterText.trim()) {
      const q = filterText.toLowerCase();
      const haystack = [
        c.instrumentName, c.instrumentType, c.customerName,
        c.orderNumber, c.intakeProtocolNumber, c.methodologyCode, c.probeCode, c.probeName,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  // Hromadné mazání kalibrací — jen admin (backend to vynucuje require_admin).
  const sel = useBulkSelect(filteredCalibrations.map(c => c.id));

  const bulkRemove = async () => {
    const ids = sel.selectedIds;
    const { ok, failed } = await bulkDelete(ids, id => api.delete(`/calibrations/${id}`));
    if (selected && ids.includes(selected)) { setSelected(null); }
    sel.clear();
    load();
    if (failed) toast.warning(`Smazáno ${ok}, ${failed} selhalo`);
    else toast.success(`Smazáno ${ok}`);
  };

  // Simulace a overlay odstraněny — měření má vlastní routu `/calibrations/:id/run`
  // (viz CalibrationRunScreen). V editoru si laborant jen připraví metodiku/etalon
  // a měření spustí přímo na dedikované obrazovce.

  // ─── Main Calibration View ───
  return (
    <div data-testid="calibration-page" className="space-y-4 h-[calc(100vh-7rem)] flex flex-col">
      {/* Lab tabs — filtrované dle 'view' permission */}
      <div className="flex items-center border-b" style={{ borderColor: 'var(--ct-border)' }}>
        {visibleLabs.length === 0 ? (
          <div className="p-3 text-sm flex items-center gap-2" style={{ color: 'var(--ct-text-disabled)' }}>
            <Lock className="w-4 h-4" />
            {t('calibrations.no-permission-labs')}
          </div>
        ) : (
          visibleLabs.map(l => (
            <button
              key={l.id}
              onClick={() => { setSelectedLab(l.id); setSelected(null); }}
              className={`ct-qty-tab ${selectedLab === l.id ? 'active' : ''}`}
              title={pendingByLab[l.id] ? `${pendingByLab[l.id]} kalibrace čeká na start` : undefined}
            >
              {l.label}
              {pendingByLab[l.id] > 0 && (
                <span
                  className="ml-1.5 inline-flex items-center justify-center text-[10px] font-bold rounded-full px-1.5 min-w-[16px]"
                  style={{ background: 'var(--ct-info, #2563eb)', color: '#fff' }}
                >
                  {pendingByLab[l.id]}
                </span>
              )}
            </button>
          ))
        )}
        {/* View toggle vpravo */}
        <div className="ml-auto pr-2 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setView('list')}
            className="p-1.5 rounded transition-colors"
            style={{
              color: viewMode === 'list' ? 'var(--ct-accent)' : 'var(--ct-text-secondary)',
              background: viewMode === 'list' ? 'rgba(var(--ct-accent-rgb), 0.1)' : 'transparent',
            }}
            title="Seznam (split-view)"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setView('kanban')}
            className="p-1.5 rounded transition-colors"
            style={{
              color: viewMode === 'kanban' ? 'var(--ct-accent)' : 'var(--ct-text-secondary)',
              background: viewMode === 'kanban' ? 'rgba(var(--ct-accent-rgb), 0.1)' : 'transparent',
            }}
            title="Kanban (sloupce dle stavu)"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>
      </div>

      {viewMode === 'kanban' ? (
        <KanbanView
          calibrations={filteredCalibrations}
          onCardClick={(c) => navigate(`/calibrations/${c.id}`)}
        />
      ) : (
      <div className="flex gap-4 flex-1 overflow-hidden">
        {/* Left: Calibration list */}
        <div className="ct-panel w-[450px] flex-shrink-0 flex flex-col">
          <div className="ct-panel-header flex items-center justify-between">
            <span>{t('calibrations.list-title', { count: filteredCalibrations.length })}</span>
            <button
              data-testid="add-calibration-btn"
              onClick={openCreateDialog}
              disabled={!canCalibrate}
              className="p-1 rounded"
              style={{ color: canCalibrate ? 'var(--ct-accent)' : 'var(--ct-text-disabled)' }}
              title={!canCalibrate ? t('calibrations.btn-add-disabled-title') : t('calibrations.btn-add-title')}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {/* Chip filter + text search. Multi-select status chips. */}
          <div className="px-2 py-2 space-y-2" style={{ borderBottom: '1px solid var(--ct-border)' }}>
            <Input
              placeholder={t('calibrations.search-placeholder')}
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              className="h-8 text-xs"
            />
            <ChipFilter
              options={KANBAN_STATUSES.map(col => ({ id: col.status, label: t(KANBAN_STATUS_LABEL_KEYS[col.status]) }))}
              value={filterStatuses}
              onChange={setFilterStatuses}
              tones={Object.fromEntries(KANBAN_STATUSES.map(col => [col.status, col.tone]))}
            />
            {user?.isAdmin && (
              <BulkDeleteBar count={sel.count} onDelete={bulkRemove} onClear={sel.clear} entityLabel="kalibrací" />
            )}
          </div>
          <ScrollArea className="flex-1">
            {filteredCalibrations.map(c => (
              <div
                key={c.id}
                className={`ct-table-row flex items-center gap-2 px-3 py-2 text-sm ${selected === c.id ? 'active' : ''} ${isRecent(c.createdAt) ? 'ct-row-new' : ''}`}
              >
              {user?.isAdmin && (
                <Checkbox
                  data-testid={`calibration-select-${c.id}`}
                  checked={sel.isSelected(c.id)}
                  onCheckedChange={() => sel.toggle(c.id)}
                  className="flex-shrink-0"
                />
              )}
              <button
                onClick={() => selectCal(c)}
                className="flex-1 min-w-0 text-left"
                title={isRecent(c.createdAt) ? t('calibrations.list-row-recent-title') : undefined}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{c.instrumentName || t('calibrations.card-instrument-fallback')}</div>
                    <div className="text-xs truncate" style={{ color: 'var(--ct-text-secondary)' }}>
                      {c.instrumentType || ''}
                      {c.probeCode && (
                        <span className="font-mono ml-2" style={{ color: 'var(--ct-accent)' }}>· {c.probeCode}</span>
                      )}
                    </div>
                    {(c.orderNumber || c.customerName) && (
                      <div className="text-xs truncate flex items-center gap-1.5 mt-0.5" style={{ color: 'var(--ct-text-disabled)' }}>
                        {c.orderNumber && <RefLink type="order" id={c.orderNumber} />}
                        {c.intakeProtocolNumber && <RefLink type="intake" id={c.intakeProtocolNumber} />}
                        {c.customerName && <span className="truncate">{c.customerName}</span>}
                      </div>
                    )}
                  </div>
                  <StatusBadge status={c.status} className="flex-shrink-0" />
                </div>
              </button>
              </div>
            ))}
          </ScrollArea>
        </div>

        {/* Right: Detail */}
        {selected ? (
          <div className="flex-1 overflow-auto space-y-4">
            {/* Rejection panel — pouze když je kalibrace vrácena k přepracování */}
            {form.status === 'rejected' && (
              <div
                data-testid="rejection-panel"
                className="ct-panel"
                style={{
                  borderColor: 'var(--ct-error)',
                  background: 'var(--ct-error-soft)',
                }}
              >
                <div
                  className="ct-panel-header flex items-center gap-2"
                  style={{ color: 'var(--ct-error)' }}
                >
                  <XCircle className="w-4 h-4" />
                  {t('calibrations.rejection-title')}
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <div
                      className="text-xs uppercase tracking-wider mb-1"
                      style={{ color: 'var(--ct-text-secondary)' }}
                    >
                      {t('calibrations.rejection-reason-label')}
                    </div>
                    <div
                      className="text-sm p-3 rounded whitespace-pre-wrap"
                      style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)' }}
                    >
                      {form.rejectionReason || t('calibrations.rejection-reason-empty')}
                    </div>
                  </div>
                  <Button
                    data-testid="start-rework-btn"
                    onClick={startRework}
                    disabled={!canCalibrate}
                    className="ct-btn-primary gap-1"
                    title={!canCalibrate ? t('calibrations.btn-add-disabled-title') : undefined}
                  >
                    <RefreshCw className="w-4 h-4" /> {t('calibrations.btn-start-rework')}
                  </Button>
                </div>
              </div>
            )}

            <div className="ct-panel">
              <div className="ct-panel-header flex items-center justify-between">
                <span>{t('calibrations.settings-title')}</span>
                <button
                  onClick={() => navigate(`/calibrations/${selected}`)}
                  className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-opacity hover:opacity-80"
                  style={{ color: 'var(--ct-accent)' }}
                  title={t('calibrations.btn-open-detail-title')}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {t('calibrations.btn-open-detail')}
                </button>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <div className="ct-form-field">
                  <label className="ct-form-label">{t('calibrations.label-instrument')}</label>
                  <div
                    className="h-9 flex items-center px-3 text-sm rounded-md border"
                    style={{ background: 'var(--ct-bg-secondary)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
                  >
                    {form.instrumentName || '—'}
                    {form.instrumentType && (
                      <span className="ml-2 font-mono text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                        {form.instrumentType}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ct-form-field">
                  <label className="ct-form-label">
                    {t('calibrations.label-probe')}
                    {probeLocked && (
                      <span className="ml-2 text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                        {t('calibrations.label-probe-locked')}
                      </span>
                    )}
                  </label>
                  {probesForInstrument.length === 0 ? (
                    <div
                      className="h-9 flex items-center px-3 text-xs rounded-md border italic"
                      style={{ background: 'var(--ct-bg-secondary)', borderColor: 'var(--ct-warning)', color: 'var(--ct-warning)' }}
                      title={t('calibrations.probe-no-probes-tooltip')}
                    >
                      {t('calibrations.probe-no-probes-warn')}
                    </div>
                  ) : (
                    <select
                      data-testid="probe-select"
                      value={form.probeId || ''}
                      onChange={e => onProbeChange(e.target.value)}
                      disabled={probeLocked}
                      className="h-9 text-sm rounded-md border px-3"
                      style={{
                        background: probeLocked ? 'var(--ct-bg-secondary)' : 'var(--ct-bg)',
                        borderColor: 'var(--ct-border)',
                        color: probeLocked ? 'var(--ct-text-disabled)' : 'var(--ct-text)',
                        cursor: probeLocked ? 'not-allowed' : 'pointer',
                      }}
                      title={probeLocked ? t('calibrations.probe-locked-tooltip') : undefined}
                    >
                      <option value="">{t('calibrations.probe-select-placeholder')}</option>
                      {probesForInstrument.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.code} — {p.name} ({p.rangeMin}–{p.rangeMax})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {form.probeId && (
                  <div className="ct-form-field col-span-2">
                    <label className="ct-form-label">{t('calibrations.label-probe-info')}</label>
                    <div
                      className="px-3 py-2 text-xs rounded-md border space-y-1"
                      style={{ background: 'var(--ct-bg-secondary)', borderColor: 'var(--ct-border)', color: 'var(--ct-text-secondary)' }}
                    >
                      <div>
                        <span className="ct-eyebrow">{t('calibrations.label-range')}</span>{' '}
                        <span className="font-mono" style={{ color: 'var(--ct-text)' }}>
                          {form.probeRangeMin}–{form.probeRangeMax} {form.probeUnitSymbol || ''}
                        </span>
                        {form.probeTypeName && <span className="ml-3 ct-eyebrow">{t('calibrations.label-type')}</span>}
                        {form.probeTypeName && <span className="ml-1" style={{ color: 'var(--ct-text)' }}>{form.probeTypeName}</span>}
                      </div>
                      <div>
                        <span className="ct-eyebrow">{t('calibrations.label-meth-from-probe')}</span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="ct-form-field">
                  <label className="ct-form-label">{t('calibrations.label-etalon')}</label>
                  <select
                    value={form.etalon || ''}
                    onChange={e => {
                      const et = etalons.find(x => x.id === e.target.value);
                      setForm({ ...form, etalon: e.target.value, etalonCode: et?.code || '' });
                    }}
                    className="h-9 text-sm rounded-md border px-3"
                    style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
                  >
                    <option value="">{t('calibrations.etalon-select-placeholder')}</option>
                    {/* Etalons filtruj dle laboratoře kalibrace
                        (= selectedLab). Pre-migration etalony bez laboratoře
                        propustíme (defenzivní fallback). */}
                    {etalons
                      .filter(e => !e.laboratory || e.laboratory === selectedLab)
                      .map(e => <option key={e.id} value={e.id}>{e.code} — {e.name}</option>)}
                  </select>
                </div>
                <div className="ct-form-field">
                  <label className="ct-form-label">{t('calibrations.label-status')}</label>
                  <select
                    value={form.status || 'pending_intake'}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                    className="h-9 text-sm rounded-md border px-3"
                    style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
                  >
                    <option value="pending_intake">{t('calibrations.status-pending-intake')}</option>
                    <option value="in_progress">{t('calibrations.status-in-progress')}</option>
                    <option value="completed">{t('calibrations.status-completed')}</option>
                    <option value="pending_approval">{t('calibrations.status-pending-approval')}</option>
                  </select>
                </div>
                <div className="ct-form-field">
                  <label className="ct-form-label">{t('calibrations.label-notes')}</label>
                  <Input value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} className="h-9 text-sm" />
                </div>
              </div>
              <div className="px-4 pb-4 flex gap-2 items-center">
                <Button
                  onClick={saveCal}
                  disabled={!canCalibrate}
                  className="ct-btn-primary gap-1"
                  title={!canCalibrate ? t('calibrations.btn-add-disabled-title') : undefined}
                >
                  <Save className="w-4 h-4" /> {t('calibrations.btn-save')}
                </Button>
                {(() => {
                  // Kalibraci nelze spustit bez sondy a etalonu.
                  // Sonda nese metodiku + přístroj — kontrolujeme jen ji + etalon.
                  const startBlockers = [];
                  if (!form.probeId) startBlockers.push(t('calibrations.field-probe-required'));
                  if (!form.etalon) startBlockers.push(t('calibrations.field-etalon-required'));
                  const canStart = canCalibrate && startBlockers.length === 0;
                  const startTitle = !canCalibrate
                    ? t('calibrations.btn-add-disabled-title')
                    : startBlockers.length > 0
                      ? t('calibrations.btn-start-disabled-fields', { fields: startBlockers.join(', ') })
                      : undefined;
                  return (
                    <>
                      <Button
                        data-testid="start-calibration-btn"
                        onClick={() => selected && navigate(`/calibrations/${selected}/run`)}
                        disabled={!canStart}
                        className="ct-btn-secondary gap-1"
                        style={{ color: 'var(--ct-accent)', borderColor: 'var(--ct-accent)' }}
                        title={startTitle}
                      >
                        <Play className="w-4 h-4" /> {t('calibrations.btn-start-cal')}
                      </Button>
                      {canCalibrate && startBlockers.length > 0 && (
                        <span className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                          {t('calibrations.btn-start-missing-prefix', { fields: startBlockers.join(', ') })}
                        </span>
                      )}
                    </>
                  );
                })()}
                {(form.status === 'completed' || form.status === 'in_progress') && (
                  <Button
                    onClick={submitForApproval}
                    disabled={!canCalibrate}
                    className="ct-btn-secondary gap-1"
                    style={{ color: 'var(--ct-warning)', borderColor: 'var(--ct-warning)' }}
                    title={!canCalibrate ? t('calibrations.btn-add-disabled-title') : undefined}
                  >
                    <Send className="w-4 h-4" /> {t('calibrations.btn-submit-approval')}
                  </Button>
                )}
                {!canCalibrate && (
                  <span className="text-xs flex items-center gap-1" style={{ color: 'var(--ct-text-disabled)' }}>
                    <Lock className="w-3 h-3" /> {t('calibrations.read-only-lab')}
                  </span>
                )}
              </div>
            </div>

            {/* Preview of measurement data */}
            {measurements.points.length > 0 && (
              <div className="ct-panel">
                <div className="ct-panel-header">{t('calibrations.preview-points-title', { count: measurements.points.length })}</div>
                <div className="p-3 flex flex-wrap gap-2">
                  {measurements.points.map((pt, i) => (
                    <span key={i} className="font-mono text-xs px-2 py-1 rounded" style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)' }}>
                      {pt.nominal}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <EmptyState
              icon={FlaskConical}
              title={t('calibrations.select-empty-title')}
              description={t('calibrations.select-empty-desc')}
            />
          </div>
        )}
      </div>
      )}

      {/* ADR-006: Dialog pro novou kalibraci — výběr příjmu + přístroje */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent
          data-testid="create-calibration-dialog"
          style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)', maxWidth: '560px' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--ct-accent)', fontFamily: 'Inter' }}>
              {t('calibrations.create-dialog-title', { lab: LABS.find(l => l.id === selectedLab)?.label })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: 'var(--ct-text-secondary)' }}>
                {t('calibrations.create-dialog-intake-label')}
              </label>
              <select
                data-testid="dialog-intake-select"
                value={dialogIntakeId}
                onChange={e => { setDialogIntakeId(e.target.value); setDialogInstrumentIdx(''); }}
                className="h-9 text-sm rounded px-2 w-full"
                style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' }}
              >
                <option value="">{t('calibrations.create-dialog-select-placeholder')}</option>
                {availableIntakes.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.intakeProtocolNumber} · {i.orderNumber || t('calibrations.create-dialog-no-order')}
                    {i.customerName ? ` · ${i.customerName}` : ''}
                    {' · '}{t('calibrations.create-dialog-instr-count', { count: (i.instruments || []).length })}
                  </option>
                ))}
              </select>
              {availableIntakes.length === 0 && (
                <p className="text-xs mt-1" style={{ color: 'var(--ct-text-disabled)' }}>
                  {t('calibrations.create-dialog-no-intakes')}
                </p>
              )}
            </div>

            {dialogIntakeId && (
              <div>
                <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: 'var(--ct-text-secondary)' }}>
                  {t('calibrations.create-dialog-instrument-label')}
                </label>
                <select
                  data-testid="dialog-instrument-select"
                  value={dialogInstrumentIdx}
                  onChange={e => setDialogInstrumentIdx(e.target.value)}
                  className="h-9 text-sm rounded px-2 w-full"
                  style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' }}
                >
                  <option value="">{t('calibrations.create-dialog-select-placeholder')}</option>
                  {dialogInstruments.map((inst, idx) => (
                    <option key={idx} value={idx}>
                      {inst.name || t('calibrations.create-dialog-instrument-no-name')}
                      {inst.type ? ` · ${inst.type}` : ''}
                      {inst.marking1 ? ` · ${inst.marking1}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={() => setCreateDialogOpen(false)} className="ct-btn-secondary">
              {t('calibrations.btn-cancel')}
            </Button>
            <Button
              data-testid="dialog-confirm-create"
              onClick={confirmCreate}
              disabled={!dialogIntakeId || dialogInstrumentIdx === ''}
              className="ct-btn-primary gap-1"
            >
              <Plus className="w-4 h-4" /> {t('calibrations.btn-create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
